import React, { useState, useEffect } from "react";
import { Plus, Pencil, Trash2, MapPin, Bell, Search, X, Check, AlertTriangle, Loader2 } from "lucide-react";
import "../styles/holidaysPage.css";
import Sidebar from "./Sidebar";
import Footer from "./Footer";
import { useAuth } from "../context/AuthContext";

// ═══════════════════════════════════════════
//  API CALLS
// ═══════════════════════════════════════════
const API_BASE = `${process.env.REACT_APP_API_BASE_URL}/api`;

const fetchHolidays = async () => {
  const res = await fetch(`${API_BASE}/holidays`);
  if (!res.ok) throw new Error("Failed to fetch holidays");
  return res.json();
};

const createHoliday = async (data) => {
  const res = await fetch(`${API_BASE}/holidays`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error("Failed to create holiday");
  return res.json();
};

const updateHoliday = async (id, data) => {
  const res = await fetch(`${API_BASE}/holidays/${id}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error("Failed to update holiday");
  return res.json();
};

const deleteHoliday = async (id) => {
  const res = await fetch(`${API_BASE}/holidays/${id}`, {
    method: "DELETE",
  });
  if (!res.ok) throw new Error("Failed to delete holiday");
  return res.json();
};

// ═══════════════════════════════════════════
//  HELPERS
// ═══════════════════════════════════════════

// API snake_case → UI camelCase
const mapHoliday = (d) => ({
  id:         d.id,
  name:       d.name,
  date:       d.holiday_date
                ? String(d.holiday_date).slice(0, 10)
                : "",
  location:   d.region || "All Locations",
  isOptional: d.is_optional ?? false,
});

// UI camelCase → API snake_case payload
const buildPayload = (formData) => ({
  name:        formData.name,
  holiday_date: formData.date,
  region:      formData.location === "All Locations" ? null : formData.location,
  client:      null,
  is_optional: formData.isOptional,
});

const DAYS   = ["Sunday","Monday","Tuesday","Wednesday","Thursday","Friday","Saturday"];
const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

function formatDate(dateStr) {
  const d = new Date(dateStr + "T00:00:00");
  const local = new Date(d.toLocaleString('en-US', { timeZone: 'Asia/Kolkata' }));
  return `${String(local.getDate()).padStart(2, "0")}-${String(local.getMonth() + 1).padStart(2, "0")}-${local.getFullYear()}`;
}

function getDayName(dateStr) {
  return DAYS[new Date(dateStr + "T00:00:00").getDay()];
}

const EMPTY_FORM = { name: "", date: "", location: "All Locations", isOptional: false };
const TODAY = new Date();

// ═══════════════════════════════════════════
//  COMPONENT
// ═══════════════════════════════════════════
export default function HolidaysPage({ isTab = false }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [holidays,    setHolidays]    = useState([]);
  const [isOpen,      setIsOpen]      = useState(false);
  const [editItem,    setEditItem]    = useState(null);
  const [search,      setSearch]      = useState("");
  const [toast,       setToast]       = useState(null);
  const [formData,    setFormData]    = useState(EMPTY_FORM);
  const [errors,      setErrors]      = useState({});
  const [deleteId,    setDeleteId]    = useState(null);
  const [loading,     setLoading]     = useState(true);
  const [submitting,  setSubmitting]  = useState(false);
  const [deleting,    setDeleting]    = useState(false);

  const toggleSidebar = () => setSidebarOpen((prev) => !prev);
  const { user, token } = useAuth();

  // ── Load all holidays on mount ──
  useEffect(() => {
    setLoading(true);
    fetchHolidays()
      .then((data) => setHolidays(data.map(mapHoliday)))
      .catch(() => showToast("Failed to load holidays", "error"))
      .finally(() => setLoading(false));
  }, []);

  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  const openCreate = () => {
    setFormData(EMPTY_FORM);
    setEditItem(null);
    setErrors({});
    setIsOpen(true);
  };

  const openEdit = (item) => {
    setFormData({
      name:       item.name,
      date:       item.date,
      location:   item.location,
      isOptional: item.isOptional,
    });
    setEditItem(item);
    setErrors({});
    setIsOpen(true);
  };

  const validate = () => {
    const e = {};
    if (!formData.name.trim()) e.name = "Holiday name is required";
    if (!formData.date)        e.date = "Date is required";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  // ── Create / Update ──
  const handleSubmit = async () => {
    if (!validate()) return;
    setSubmitting(true);
    try {
      if (editItem) {
        // PUT → returns updated object directly
        const updated = await updateHoliday(editItem.id, buildPayload(formData));
        setHolidays((prev) =>
          prev.map((h) => (h.id === editItem.id ? mapHoliday(updated) : h))
        );
        showToast("Holiday updated successfully!");
      } else {
        // POST → returns new object directly
        const created = await createHoliday(buildPayload(formData));
        setHolidays((prev) => [mapHoliday(created), ...prev]);
        showToast("Holiday added successfully!");
      }
      setIsOpen(false);
    } catch {
      showToast("Something went wrong!", "error");
    } finally {
      setSubmitting(false);
    }
  };

  // ── Delete ──
  const handleDelete = async () => {
    setDeleting(true);
    try {
      await deleteHoliday(deleteId);
      setHolidays((prev) => prev.filter((h) => h.id !== deleteId));
      setDeleteId(null);
      showToast("Holiday deleted!", "error");
    } catch {
      showToast("Delete failed!", "error");
    } finally {
      setDeleting(false);
    }
  };

  const sorted = [...holidays]
    .filter((h) => h.name.toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => new Date(a.date) - new Date(b.date));

  // ═══════════════════════════════════════════
  //  RENDER
  // ═══════════════════════════════════════════
  const pageContent = (
    <>
    <div className="hp-wrap" style={isTab ? { padding: 0 } : {}}>
      {!isTab && (
        <header className="hp-topbar">
          <div className="hp-topbar-left">
            <h1>Holiday Management</h1>
            <p>Manage company holidays and optional days off</p>
          </div>
          <div className="hp-topbar-right">
            <div className="hp-search-wrap">
              <Search size={14} className="hp-search-icon" />
              <input
                className="hp-search-input"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search holidays..."
              />
            </div>
            <div className="hp-bell">
              <Bell size={20} />
              <span className="hp-bell-badge">3</span>
            </div>
          </div>
        </header>
      )}

      <main className="hp-content" style={isTab ? { padding: 0, marginTop: 0 } : {}}>
              <div className="hp-section-header">
                <div>
                  <h2>2026 Holidays</h2>
                  <p>{holidays.length} holidays configured</p>
                </div>
                <button className="hp-add-btn" onClick={openCreate}>
                  <Plus size={15} />
                  Add Holiday
                </button>
              </div>

              <div className="hp-table-card">
                {loading ? (
                  <div style={{ padding: "3rem", textAlign: "center", color: "#888", display: "flex", alignItems: "center", justifyContent: "center", gap: "8px" }}>
                    <Loader2 size={18} style={{ animation: "spin 1s linear infinite" }} />
                    Loading holidays...
                  </div>
                ) : (
                  <table className="hp-table">
                    <thead>
                      <tr>
                        {["Holiday Name", "Date", "Day", "Location", "Type", "Actions"].map((col) => (
                          <th key={col}>{col}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {sorted.length === 0 ? (
                        <tr className="empty-row">
                          <td colSpan={6}>No holidays found.</td>
                        </tr>
                      ) : (
                        sorted.map((h) => (
                          <tr key={h.id} className={new Date(h.date) < TODAY ? "is-past" : ""}>
                            <td className="col-name">{h.name}</td>
                            <td className="col-date">{formatDate(h.date)}</td>
                            <td className="col-day">{getDayName(h.date)}</td>
                            <td>
                              <span className="col-location">
                                <MapPin size={13} />
                                {h.location}
                              </span>
                            </td>
                            <td>
                              {h.isOptional
                                ? <span className="badge badge-optional">Optional</span>
                                : <span className="badge badge-mandatory">Mandatory</span>
                              }
                            </td>
                            <td>
                              <div className="action-btns">
                                <button className="action-btn edit" onClick={() => openEdit(h)} title="Edit">
                                  <Pencil size={14} />
                                </button>
                                <button className="action-btn del" onClick={() => setDeleteId(h.id)} title="Delete">
                                  <Trash2 size={14} />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                )}
              </div>
            </main>
          </div>

          {/* ── Add / Edit Modal ── */}
          {isOpen && (
            <div className="hp-overlay">
              <div className="hp-modal">
                <div className="hp-modal-header">
                  <div>
                    <h3>{editItem ? "Edit Holiday" : "Add Holiday"}</h3>
                    <p>{editItem ? "Update holiday details" : "Add a new company holiday"}</p>
                  </div>
                  <button className="hp-modal-close" onClick={() => setIsOpen(false)}>
                    <X size={18} />
                  </button>
                </div>

                <div className="hp-modal-body">
                  <div className="form-group">
                    <label>Holiday Name <span>*</span></label>
                    <input
                      className={`form-input ${errors.name ? "has-error" : ""}`}
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="e.g. Independence Day"
                    />
                    {errors.name && <p className="form-error">{errors.name}</p>}
                  </div>

                  <div className="form-group">
                    <label>Date <span>*</span></label>
                    <input
                      type="date"
                      className={`form-input ${errors.date ? "has-error" : ""}`}
                      value={formData.date}
                      onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    />
                    {errors.date && <p className="form-error">{errors.date}</p>}
                  </div>

                  <div className="form-group">
                    <label>Location</label>
                    <select
                      className="form-input"
                      value={formData.location}
                      onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                    >
                      <option>All Locations</option>
                      <option>India</option>
                      <option>US</option>
                      <option>UK</option>
                    </select>
                  </div>

                  <div className="toggle-row">
                    <span className="toggle-label">Optional Holiday</span>
                    <button
                      className={`toggle-track ${formData.isOptional ? "on" : "off"}`}
                      onClick={() => setFormData({ ...formData, isOptional: !formData.isOptional })}
                    >
                      <div className={`toggle-thumb ${formData.isOptional ? "on" : "off"}`} />
                    </button>
                  </div>
                </div>

                <div className="hp-modal-footer">
                  <button
                    className="btn-cancel"
                    onClick={() => setIsOpen(false)}
                    disabled={submitting}
                  >
                    Cancel
                  </button>
                  <button
                    className="btn-primary"
                    onClick={handleSubmit}
                    disabled={submitting}
                  >
                    {submitting ? (
                      <>
                        <Loader2 size={14} style={{ animation: "spin 1s linear infinite" }} />
                        {editItem ? "Saving..." : "Adding..."}
                      </>
                    ) : (
                      editItem ? "Save Changes" : "Add Holiday"
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* ── Delete Confirm ── */}
          {deleteId && (
            <div className="hp-overlay">
              <div className="hp-delete-modal">
                <div className="hp-delete-icon">
                  <AlertTriangle size={24} />
                </div>
                <h3>Delete Holiday?</h3>
                <p>This action cannot be undone.</p>
                <div className="hp-delete-actions">
                  <button
                    className="btn-cancel"
                    onClick={() => setDeleteId(null)}
                    disabled={deleting}
                  >
                    Cancel
                  </button>
                  <button
                    className="btn-danger"
                    onClick={handleDelete}
                    disabled={deleting}
                  >
                    {deleting ? (
                      <>
                        <Loader2 size={14} style={{ animation: "spin 1s linear infinite" }} />
                        Deleting...
                      </>
                    ) : (
                      "Delete"
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* ── Toast ── */}
          {toast && (
            <div className={`hp-toast ${toast.type}`}>
              <Check size={14} />
              {toast.msg}
            </div>
          )}
    </>
  );

  if (isTab) return pageContent;

  return (
    <>
      <div className="page container">
        {sidebarOpen && <div className="overlay" onClick={toggleSidebar}></div>}
        <Sidebar sidebarOpen={sidebarOpen} toggleSidebar={toggleSidebar} />
        <main className="main">
          <div className="hamburger" onClick={toggleSidebar}>☰</div>
          {pageContent}
          <Footer />
        </main>
      </div>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to   { transform: rotate(360deg); }
        }
      `}</style>
    </>
  );
}